import { z } from 'zod';

/**
 * Schema for a single observation in document evaluation
 */
export const ObservationSchema = z.object({
  section: z.string().describe('The section or area being evaluated'),
  detail: z.string().describe('Detailed observation or feedback for this section'),
});

export type Observation = z.infer<typeof ObservationSchema>;

/**
 * Schema for observation buckets (categorized feedback)
 */
export const ObservationBucketsSchema = z.object({
  good: z.array(ObservationSchema).default([]).describe('Aspects that are well done'),
  okay: z.array(ObservationSchema).default([]).describe('Aspects that are acceptable'),
  need_improvement: z
    .array(ObservationSchema)
    .default([])
    .describe('Aspects that need improvement'),
  not_analyzed: z
    .array(ObservationSchema)
    .default([])
    .describe('Aspects that could not be analyzed'),
  missing: z.array(ObservationSchema).default([]).describe('Aspects that are missing'),
});

export type ObservationBuckets = z.infer<typeof ObservationBucketsSchema>;

/**
 * Schema for the structured evaluation output from LLM
 */
export const EvaluationStructuredOutputSchema = z.object({
  overall_score: z.number().int().min(0).max(100).describe('Overall quality score from 0 to 100'),
  observations: ObservationBucketsSchema.describe('Categorized observations and feedback'),
});

export type EvaluationStructuredOutput = z.infer<typeof EvaluationStructuredOutputSchema>;

/**
 * Schema for the complete evaluation result
 */
export const EvaluationResultSchema = EvaluationStructuredOutputSchema.extend({
  key: z.string().nullable().optional().describe('Document type key used for evaluation'),
  persona: z.string().nullable().optional().describe('Persona applied for evaluation'),
  usage: z
    .object({
      model: z.string().optional(),
      inputTokens: z.number().int().nonnegative(),
      outputTokens: z.number().int().nonnegative(),
      totalTokens: z.number().int().nonnegative(),
    })
    .optional()
    .describe('Token usage captured for this evaluation run'),
});

export type EvaluationResult = z.infer<typeof EvaluationResultSchema>;

/**
 * Schema for evaluation request
 */
export const EvaluationRequestSchema = z.object({
  proposal_id: z.string().uuid().describe('Proposal ID to evaluate'),
  content: z.string().describe('Document content of the document to evaluate'),
  category: z
    .string()
    .describe('Category/render type of the project (document, presentation, webpage)'),
  document_type: z.string().optional().describe('Document type key for evaluation rubric'),
});

export type EvaluationRequest = z.infer<typeof EvaluationRequestSchema>;

/**
 * Schema for evaluation response
 */
export const EvaluationResponseSchema = EvaluationResultSchema;

export type EvaluationResponse = z.infer<typeof EvaluationResponseSchema>;

/**
 * Schema for description generation request
 */
export const GenerateDescriptionRequestSchema = z.object({
  markdown: z.string().describe('Markdown content of the document to generate description for'),
});

export type GenerateDescriptionRequest = z.infer<typeof GenerateDescriptionRequestSchema>;

/**
 * Schema for description generation response
 */
export const GenerateDescriptionResponseSchema = z.object({
  description: z.string().describe('Generated description for the document'),
});

export type GenerateDescriptionResponse = z.infer<typeof GenerateDescriptionResponseSchema>;
